import { useCallback, useContext } from 'react'

import {
  setUser,
  apiUsersStatusStart,
  apiUsersStatusSuccess,
  apiUsersStatusFailure,
  apiUsersSignInStart,
  apiUsersSignInSuccess,
  apiUsersSignInFailure,
  apiUsersSignUpStart,
  apiUsersSignUpSuccess,
  apiUsersSignUpFailure
} from '../actions/users'
import { context } from '../context'
import { settings } from '../constants'
import { AnalyticsService } from '../../services/analytics'
import { readJson, serverError, customerMessage } from '../../lib/api-json'

export const useUsersApi = () => {
  const { dispatch } = useContext(context)

  const apiUserSignIn = useCallback(
    ({ email, password }) => {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      }

      dispatch(apiUsersSignInStart())
      fetch(`${settings.host}/api/v2/users/sign_in`, options)
        .then(readJson)
        .then(json => {
          const { status } = json

          if (status !== 'ok') throw serverError(json, 'Login failed. Please check email & password')

          const { data: user = {} } = json
          const { token } = user

          dispatch(setUser(user))
          window.localStorage.setItem('token', token)
          window.localStorage.removeItem('isGuest')
          dispatch(apiUsersSignInSuccess())
        })
        .catch(error => {
          dispatch(apiUsersSignInFailure(customerMessage(error, 'Login failed. Please try again.')))
        })
    },
    [dispatch]
  )

  const apiUserSignUp = useCallback(
    ({ email, firstName, lastName, mobile, password, optedInEmailMarketing, optedInWhatsappMarketing }) => {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName, mobile, password, opted_in_email_marketing: optedInEmailMarketing, opted_in_whatsapp_marketing: optedInWhatsappMarketing === true })
      }

      dispatch(apiUsersSignUpStart())
      fetch(`${settings.host}/api/v2/users/sign_up`, options)
        .then(readJson)
        .then(json => {
          const { status } = json

          if (status !== 'ok') throw serverError(json, 'Sign up failed. Please try again')

          const { data: user = {} } = json
          const { token } = user

          dispatch(setUser(user))
          window.localStorage.setItem('token', token)
          window.localStorage.removeItem('isGuest')
          dispatch(apiUsersSignUpSuccess())
        })
        .catch(error => {
          dispatch(apiUsersSignUpFailure(customerMessage(error, 'Sign up failed. Please try again.')))
        })
    },
    [dispatch]
  )

  const apiGuestSignUp = useCallback(
    ({ email, firstName, lastName, mobile, optedInWhatsappMarketing }) => {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName, mobile, opted_in_whatsapp_marketing: optedInWhatsappMarketing === true })
      }

      return fetch(`${settings.host}/api/v2/users/guest_signup`, options)
        .then(readJson)
        .then(json => {
          const { status } = json

          if (status !== 'ok') throw serverError(json, 'Unable to create guest account. Please try again.')

          const { data: user = {} } = json
          const { token } = user

          // Store the token for API auth but do NOT set the user in state -
          // guest users should not appear "signed in" in the UI.
          window.localStorage.setItem('token', token)
          window.localStorage.setItem('isGuest', 'true')
          // Guests never pass through SET_USER, so identify here (ANALYTICS.md §9).
          // No reset(): this is mid-funnel, and resetting would split the
          // visitor's journey in two. identify() resets on its own when the
          // device is still identified as a different person.
          AnalyticsService.identify(user)

          return user
        })
    },
    [dispatch]
  )

  // The server decides who this is from the Google token, not from anything
  // we say about it: `credential` (the One Tap ID token) or `access_token`
  // (the OAuth popup) is the only part that identifies the customer. The
  // names ride along for display. Sending an email here would be ignored.
  const apiUserGoogleLogin = useCallback(
    ({ credential, access_token, first_name, last_name }) => {
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, access_token, first_name, last_name })
      }

      console.log('[apiUserGoogleLogin] Posting to /api/v2/users/google_login')
      dispatch(apiUsersSignInStart())
      fetch(`${settings.host}/api/v2/users/google_login`, options)
        .then(response => {
          console.log('[apiUserGoogleLogin] Response status:', response.status)
          if (!response.ok) {
            return response.text().then(text => {
              throw new Error(`HTTP ${response.status}: ${text}`)
            })
          }
          return response.json()
        })
        .then((json = {}) => {
          const { status } = json

          if (status !== 'ok') throw serverError(json, 'Google sign-in failed. Please try again.')

          const { data: user = {} } = json
          const { token } = user

          dispatch(setUser(user))
          window.localStorage.setItem('token', token)
          window.localStorage.removeItem('isGuest')
          dispatch(apiUsersSignInSuccess())
        })
        .catch(error => {
          console.error('[apiUserGoogleLogin] Error:', error)
          dispatch(apiUsersSignInFailure(customerMessage(error, 'Google sign-in failed. Please try again.')))
        })
    },
    [dispatch]
  )

  const apiUserStatus = useCallback(() => {
    const token = window.localStorage.getItem('token')
    const isGuest = window.localStorage.getItem('isGuest') === 'true'

    dispatch(apiUsersStatusStart())
    if (!token || isGuest) return dispatch(apiUsersStatusFailure('User is not signed in.'))

    fetch(`${settings.host}/api/v2/users/status`, { headers: { 'X-User-Token': token } })
      .then(response => (response.ok ? response.json() : {}))
      .then(({ data: user = null } = {}) => {
        dispatch(setUser(user))
        dispatch(apiUsersStatusSuccess())
      })
      .catch(error => {
        dispatch(apiUsersStatusFailure(`userStatus error: ${JSON.stringify(error)}`))
      })
  }, [dispatch])

  const apiUserForgotPassword = useCallback(email => {
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    }

    return fetch(`${settings.host}/api/v2/users/forgot_password`, options)
      .then(response => response.json())
      .then((json = {}) => {
        const { status } = json

        if (status !== 'ok') {
          return 'not_found'
        }

        return 'success'
      })
      .catch(error => {
        return 'error'
      })
  }, [])

  const apiUserUpdate = useCallback(
    params => {
      const options = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Token': window.localStorage.getItem('token')
        },
        body: JSON.stringify(params)
      }

      return fetch(`${settings.host}/api/v2/users/update`, options)
        .then(response => response.json())
        .then((json = {}) => {
          const { status, data: user = null } = json

          status === 'ok' && dispatch(setUser(user))

          return status
        })
        .catch(error => {
          return 'error'
        })
    },
    [dispatch]
  )

  return {
    apiUserSignIn,
    apiUserSignUp,
    apiGuestSignUp,
    apiUserStatus,
    apiUserGoogleLogin,
    apiUserForgotPassword,
    apiUserUpdate
  }
}
